// NumberedPagination — presentation primitive.
// Prop-driven compact numbered pagination with previous/next edges.
// Router-free, API-free, Redux-free, selector-free.
//
// Props:
//   currentPage:   number
//   totalPages:    number
//   hasPrevious:   boolean   (caller edge truth; AND'd with numeric bounds)
//   hasNext:       boolean
//   onPageChange:  (page: number) => void
//   ariaLabel:     string    (required consumer-supplied nav label)
//   disabled:      boolean   (global busy — disables all navigation)
//   className:     string    (optional extra class on <nav>)

import {
  buildCompactPaginationItems,
  normalizeCurrentPage,
  normalizeTotalPages,
} from "./paginationModel";

function NumberedPagination({
  currentPage,
  totalPages,
  hasPrevious,
  hasNext,
  onPageChange,
  ariaLabel,
  disabled = false,
  className = "",
}) {
  const total = normalizeTotalPages(totalPages);
  const current = normalizeCurrentPage(currentPage, total);

  if (total <= 1) {
    return null;
  }

  const items = buildCompactPaginationItems(current, total);
  const busy = Boolean(disabled);
  const canGoPrevious = Boolean(hasPrevious) && current > 1 && !busy;
  const canGoNext = Boolean(hasNext) && current < total && !busy;

  const requestPage = (page) => {
    if (busy) return;
    if (typeof onPageChange !== "function") return;
    if (!Number.isSafeInteger(page)) return;
    if (page < 1 || page > total) return;
    if (page === current) return;
    onPageChange(page);
  };

  const classes = ["ui-pagination", className].filter(Boolean).join(" ");

  return (
    <nav className={classes} aria-label={ariaLabel}>
      <button
        type="button"
        className="ui-pagination__edge"
        onClick={() => requestPage(current - 1)}
        disabled={!canGoPrevious}
        aria-label="Попередня сторінка"
      >
        <i className="fas fa-chevron-left" aria-hidden="true" />
        <span className="ui-pagination__edge-label">Попередня</span>
      </button>

      <div className="ui-pagination__pages">
        {items.map((item) => {
          if (item.type === "ellipsis") {
            return (
              <span
                key={item.key}
                className="ui-pagination__ellipsis"
                aria-hidden="true"
              >
                …
              </span>
            );
          }

          const isCurrent = item.page === current;
          const pageClasses = [
            "ui-pagination__button",
            isCurrent ? "ui-pagination__button--current" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              key={item.page}
              type="button"
              className={pageClasses}
              onClick={() => requestPage(item.page)}
              disabled={busy}
              aria-current={isCurrent ? "page" : undefined}
              aria-label={`Сторінка ${item.page}`}
            >
              {item.page}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="ui-pagination__edge"
        onClick={() => requestPage(current + 1)}
        disabled={!canGoNext}
        aria-label="Наступна сторінка"
      >
        <span className="ui-pagination__edge-label">Наступна</span>
        <i className="fas fa-chevron-right" aria-hidden="true" />
      </button>
    </nav>
  );
}

export default NumberedPagination;
